import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n-js';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import { TEMPLATES } from '../../constants';
import { AnalyticsProvider, DateProvider } from '../../providers';
import { getRevenue } from '../../reducers/User/actions';
import { getOverlays, removeOverlay } from '../../reducers/Overlays/actions';
import {
  Card,
  CategoryCard,
  EarnedCard,
  FeaturedCard,
  TemplateDialog,
} from '../../components';
import { subathonFeaturedContent } from './constant';
import { CategoryContainer, Container, useStyles } from './styles';

const {
  TEMPLATES: TEMPLATES_DATA,
  TEMPLATES_GROUP,
  TEMPLATES_IMAGES,
} = TEMPLATES;

function Dashboard() {
  const [templateDataDialog, setTemplateDataDialog] = useState(null);
  const overlays = useSelector((state) => state.overlays);
  const user = useSelector((state) => state.user);
  const {
    data: userData,
    revenue: { referral: revenueReferral, subathon: revenueSubathon },
  } = user;
  const { data: overlaysData } = overlays;
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const highlighted1 = TEMPLATES_DATA?.soccer?.[0];
  const highlighted2 = TEMPLATES_DATA?.countdown?.[0];
  const highlighted3 = TEMPLATES_DATA?.soccer?.[2];
  const more1 = TEMPLATES_DATA?.countdown?.[1];
  const more2 = TEMPLATES_DATA?.medialoop?.[1];
  const more3 = TEMPLATES_DATA?.wordguessing?.[0];
  const [myOverlay1, myOverlay2, myOverlay3] = overlaysData;

  useEffect(() => {
    AnalyticsProvider.hit('/dashboard');
  }, []);

  useEffect(() => {
    if (userData.id) {
      dispatch(getOverlays());
    }
  }, [dispatch, userData.id]);
  useEffect(() => {
    if (userData.id) {
      dispatch(getRevenue());
    }
  }, [dispatch, userData.id]);

  const onClickMyOverlay = (id, category, type) => {
    history.push(`/builder/${id}`);
  };
  const onClickDeleteOverlay = (id) => {
    dispatch(removeOverlay(id));
  };
  const onClickTemplate = (data, highlight) => {
    AnalyticsProvider.event('CLICK_DASHBOARD_TEMPLATE', {
      key: data.id,
      highlight,
    });
    setTemplateDataDialog(data);
  };
  const onClickCategory = (category) => {
    AnalyticsProvider.event('CLICK_DASHBOARD_CATEGORY', { key: category });
    history.push(`/template/${category}`);
  };
  const onClickSeeMoreOverlays = () => {
    AnalyticsProvider.event('CLICK_SEE_MORE_OVERLAYS');
    history.push('/templates');
  };
  const onClickSeeMyOverlays = () => {
    AnalyticsProvider.event('CLICK_SEE_MY_OVERLAYS');
    history.push('/my-overlays');
  };
  const onClickSeeMyWallet = () => {
    AnalyticsProvider.event('CLICK_SEE_MY_WALLET');
    history.push('/wallet');
  };
  const onClickFromReferral = () => {
    AnalyticsProvider.event('CLICK_EARNED_REFERRAL');
    history.push('/revenue');
  };
  const onClickFromSubathon = () => {
    AnalyticsProvider.event('CLICK_EARNED_SUBATHON');
    history.push('/template/countdown');
  };
  const onClickFromSubathonFeatured = () => {
    AnalyticsProvider.event('CLICK_FEATURED_SUBATHON');
    history.push('/template/countdown');
  };

  return (
    <>
      <Box mb={4}>
        {userData.id && (
          <>
            <Box mb={2} mt={4} className={classes.header}>
              <Typography variant="h4" component="h2">
                {i18n.t('screens.dashboard.earnedThisMonth')}
              </Typography>
              <div
                className={classes.subtitleContainer}
                onClick={onClickSeeMyWallet}
              >
                <Typography className={classes.subtitle} variant="body2">
                  {i18n.t('screens.dashboard.seeMyWallet')}
                </Typography>
                <ArrowForwardIcon className={classes.icon} fontSize="small" />
              </div>
            </Box>
            <CategoryContainer>
              <EarnedCard
                description={i18n.t('screens.dashboard.fromReferral')}
                Icon={AttachMoneyIcon}
                label={revenueReferral?.total || '0.00'}
                onClick={onClickFromReferral}
              />
              <EarnedCard
                description={i18n.t('screens.dashboard.fromSubathons')}
                Icon={AttachMoneyIcon}
                label={
                  revenueSubathon?.total
                    ? Math.floor(revenueSubathon?.total * 100) / 100
                    : '0.00'
                }
                onClick={onClickFromSubathon}
              />
            </CategoryContainer>
            <Box mb={2} mt={4} className={classes.header}>
              <Typography variant="h4" component="h2">
                {i18n.t('screens.dashboard.myOverlays')}
              </Typography>
              <div
                className={classes.subtitleContainer}
                onClick={onClickSeeMyOverlays}
              >
                <Typography className={classes.subtitle} variant="body2">
                  {i18n.t('screens.dashboard.seeMyOverlays')}
                </Typography>
                <ArrowForwardIcon className={classes.icon} fontSize="small" />
              </div>
            </Box>
            <Container>
              {myOverlay1 && (
                <Card
                  createdAt={DateProvider.format(myOverlay1?.createdAt)}
                  key={myOverlay1?._id}
                  description={myOverlay1?.settings?.description}
                  hasMenu
                  image={TEMPLATES_IMAGES[myOverlay1?.key]}
                  onClick={() =>
                    onClickMyOverlay(
                      myOverlay1?._id,
                      myOverlay1?.category,
                      myOverlay1?.type
                    )
                  }
                  onDelete={() => onClickDeleteOverlay(myOverlay1?._id)}
                  title={myOverlay1?.settings?.title}
                />
              )}
              {myOverlay2 && (
                <Card
                  createdAt={DateProvider.format(myOverlay2?.createdAt)}
                  key={myOverlay2?._id}
                  description={myOverlay2?.settings?.description}
                  hasMenu
                  image={TEMPLATES_IMAGES[myOverlay2?.key]}
                  onClick={() =>
                    onClickMyOverlay(
                      myOverlay2?._id,
                      myOverlay2?.category,
                      myOverlay2?.type
                    )
                  }
                  onDelete={() => onClickDeleteOverlay(myOverlay2?._id)}
                  title={myOverlay2?.settings?.title}
                />
              )}
              {myOverlay3 && (
                <Card
                  createdAt={DateProvider.format(myOverlay3?.createdAt)}
                  key={myOverlay3?._id}
                  description={myOverlay3?.settings?.description}
                  hasMenu
                  image={TEMPLATES_IMAGES[myOverlay3?.key]}
                  onClick={() =>
                    onClickMyOverlay(
                      myOverlay3?._id,
                      myOverlay3?.category,
                      myOverlay3?.type
                    )
                  }
                  onDelete={() => onClickDeleteOverlay(myOverlay3?._id)}
                  title={myOverlay3?.settings?.title}
                />
              )}
            </Container>
          </>
        )}
        <Box mb={2} mt={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <FeaturedCard
            {...subathonFeaturedContent}
            onClickFromSubathonFeatured={onClickFromSubathonFeatured}
          />
        </Box>
        <Box mb={2} mt={4} className={classes.header}>
          <Typography variant="h4" component="h2">
            {i18n.t('screens.dashboard.highlights')}
          </Typography>
        </Box>
        <Container>
          <Card
            description={highlighted1.shortDescription}
            image={highlighted1?.images?.[0]}
            link={highlighted1?.link}
            onClick={() => onClickTemplate(highlighted1, true)}
            tier={highlighted1.tier}
            title={highlighted1.title}
            users={highlighted1.users}
          />
          <Card
            description={highlighted2.shortDescription}
            image={highlighted2?.images?.[0]}
            link={highlighted2?.link}
            onClick={() => onClickTemplate(highlighted2, true)}
            tier={highlighted2.tier}
            title={highlighted2.title}
            users={highlighted2.users}
          />
          <Card
            description={highlighted3.shortDescription}
            image={highlighted3?.images?.[0]}
            link={highlighted3?.link}
            onClick={() => onClickTemplate(highlighted3, true)}
            tier={highlighted3.tier}
            title={highlighted3.title}
            users={highlighted3.users}
          />
        </Container>
        <Box mb={2} mt={6} className={classes.header}>
          <Typography variant="h4" component="h2">
            {i18n.t('screens.dashboard.categories')}
          </Typography>
        </Box>
        <CategoryContainer>
          {TEMPLATES_GROUP.map(({ Icon, id, title }) => (
            <CategoryCard
              Icon={Icon}
              key={id}
              label={title}
              onClick={() => onClickCategory(id)}
            />
          ))}
        </CategoryContainer>
        <Box mb={2} mt={6} className={classes.header}>
          <Typography variant="h4" component="h2">
            {i18n.t('screens.dashboard.moreOverlays')}
          </Typography>
          <div
            className={classes.subtitleContainer}
            onClick={onClickSeeMoreOverlays}
          >
            <Typography className={classes.subtitle} variant="body2">
              {i18n.t('screens.dashboard.seeMoreOverlays')}
            </Typography>
            <ArrowForwardIcon className={classes.icon} fontSize="small" />
          </div>
        </Box>
        <Container>
          <Card
            description={more1.shortDescription}
            image={more1?.images?.[0]}
            link={more1?.link}
            onClick={() => onClickTemplate(more1)}
            tier={more1.tier}
            title={more1.title}
            users={more1.users}
          />
          <Card
            description={more2.shortDescription}
            image={more2?.images?.[0]}
            link={more2?.link}
            onClick={() => onClickTemplate(more2)}
            tier={more2.tier}
            title={more2.title}
            users={more2.users}
          />
          <Card
            description={more3.shortDescription}
            image={more3?.images?.[0]}
            link={more3?.link}
            onClick={() => onClickTemplate(more3)}
            tier={more3.tier}
            title={more3.title}
            users={more3.users}
          />
        </Container>
      </Box>
      <TemplateDialog
        data={templateDataDialog}
        open={!!templateDataDialog}
        onClose={() => setTemplateDataDialog(null)}
      />
    </>
  );
}

export default Dashboard;
